import { Box, Text } from '@pancakeswap/uikit'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { fmtDate, Pill } from '../ui'
import { LOCK_CLAIM } from '../routes'
import { poolInfoLink } from '../poolLinks'
import { lockCreateHref, lockLine, PairFacts, poolName, ZERO_LINE } from './lockCopy'

export type LiquidityLock = {
  status: 'locked' | 'partial' | 'unlocked' | 'unavailable' | 'checking'
  kind?: 'v2' | 'v3' | null
  shareBps?: number
  until?: number | null
  lockContract?: string | null
  checkedAt?: number
  /** an admin approved a "locked elsewhere" claim for this pool: the lock is with another locker, checked by hand */
  verifiedManually?: boolean
}

const pct = (bps?: number) => `${Math.round((bps ?? 0) / 100)}%`
const statusOf = (lock?: LiquidityLock) => lock?.status ?? 'checking'

const Pop = styled.span`
  position: relative;
  display: inline-flex;
  .pop {
    position: absolute;
    z-index: 30;
    top: calc(100% + 8px);
    left: 0;
    width: 260px;
    max-width: calc(100vw - 48px);
    padding: 12px 14px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 19px;
    font-weight: 400;
    text-align: left;
    color: #e6ecff;
    background: #101736;
    border: 1px solid rgba(127, 212, 232, 0.4);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.5);
  }
`
const PillButton = styled.button`
  all: unset;
  cursor: pointer;
  border-radius: 999px;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const popText = (lock?: LiquidityLock, pool?: string, pair?: PairFacts) => {
  const s = statusOf(lock)
  if (lock?.verifiedManually) return 'Locked with another locker. The CADINU team checked it by hand. This is not read from the chain.'
  if (pool && lock && (s === 'locked' || s === 'partial')) return lockLine(lock, pool, pair ?? {})
  if (s === 'locked') return `${pct(lock?.shareBps)} of this pool's ${lock?.kind === 'v3' ? 'active liquidity' : 'LP supply'} is held in CADINU Lock${lock?.until ? `, at least until ${fmtDate(lock.until).slice(0, 10)}` : ''}. Checked on-chain.`
  if (s === 'partial') return `Only ${pct(lock?.shareBps)} of the pool is locked in CADINU Lock. We call a pool locked from 50%.`
  return ''
}

/**
 * The lock badge of a competition pool: "LP locked" (with its share; tap for the share, the unlock date and where it was checked), "LP locked (verified manually)"
 * after an admin approved a "locked elsewhere" claim, or "Not locked". The tick is shown ONLY for a verified or manually approved lock; every other state is
 * neutral and says what is known. Computed on-chain by the API against CADINU's own lock contracts (V2 LP and V3 positions).
 */
export function LockPill({ lock, pool, pair }: { lock?: LiquidityLock; pool?: string; pair?: PairFacts }) {
  const s = statusOf(lock)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return undefined
    const close = (e: Event) => {
      if (e.type === 'keydown' ? (e as KeyboardEvent).key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])
  if (s === 'checking') return <Pill $tone="muted" data-testid="v3-lock" data-lock-status="checking">Checking LP lock…</Pill>
  if (s === 'unavailable') return <Pill $tone="muted" data-testid="v3-lock" data-lock-status="unavailable">Lock status unavailable</Pill>
  if (s === 'unlocked') return <Pill $tone="muted" data-testid="v3-lock" data-lock-status="unlocked">Not locked</Pill>
  const label = lock?.verifiedManually ? '✓ LP locked (verified manually)' : s === 'locked' ? `✓ LP locked · ${pct(lock?.shareBps)}` : `Partly locked · ${pct(lock?.shareBps)}`
  return (
    <Pop ref={ref}>
      <PillButton type="button" aria-expanded={open} aria-haspopup="true" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v) }} data-testid="v3-lock-btn">
        <Pill $tone={s === 'locked' ? 'success' : 'muted'} data-testid="v3-lock" data-lock-status={s} data-manual={lock?.verifiedManually ? 'true' : undefined}>{label}</Pill>
      </PillButton>
      {open && <span className="pop" role="status" data-testid="v3-lock-pop">{popText(lock, pool, pair)}</span>}
    </Pop>
  )
}

/**
 * One fixed-height row under the pills so cards line up: "Lock this pool" (primary) and the manual-check path for a lock with another locker
 * (secondary, small), or the unlock date, or nothing. Sits above a card's stretched link. "Lock this pool" opens the Lock create flow with
 * this pool preselected (?pool=).
 */
export function LockLink({ lock, pool, compact = true }: { lock?: LiquidityLock; pool?: string; compact?: boolean }) {
  const s = statusOf(lock)
  const open = s === 'unlocked' || s === 'partial'
  return (
    <Text fontSize="13px" color="textSubtle" style={{ position: 'relative', zIndex: 2, minHeight: 44, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 14 }}>
      {open && (
        <Link
          href={lockCreateHref(pool)}
          style={{ textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', minHeight: 44, color: 'inherit', fontWeight: 600 }}
          data-testid="v3-lock-link"
        >
          Lock this pool
        </Link>
      )}
      {open && pool && (
        <Link href={`${LOCK_CLAIM}?pool=${pool}`} style={{ textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', minHeight: 44, color: 'inherit', fontSize: 12 }} data-testid="v3-lock-elsewhere">
          {compact ? 'Other locker? Ask for a check' : 'Locked with another locker? Ask for a manual check'}
        </Link>
      )}
      {s === 'locked' && lock?.until ? <span>Locked until {fmtDate(lock.until).slice(0, 10)}</span> : null}
    </Text>
  )
}

/** The counted pool, named with both symbols, the fee tier and a short address; a link to its pool page when its exchange is known. */
function CountedPool({ pool, pair }: { pool: string; pair: PairFacts & { dexName?: string } }) {
  const name = poolName(pool, pair)
  const link = pair.dexName ? poolInfoLink(pair.dexName, pool) : null
  return (
    <Text fontSize="12px" color="textSubtle" mt="6px" data-testid="v3-lock-pool">
      Counted pool:{' '}
      {link ? (
        <Link href={link.href} target={link.external ? '_blank' : undefined} rel={link.external ? 'noreferrer' : undefined} style={{ textDecoration: 'underline', color: 'inherit' }}>
          {name}
        </Link>
      ) : (
        name
      )}
    </Text>
  )
}

/** Detail-page version: the pill, the counted pool, its locked share and until-date, and what to do when it is not locked. */
export default function LockBadge({ lock, pool, pair }: { lock?: LiquidityLock; pool?: string; pair?: PairFacts & { dexName?: string } }) {
  const s = statusOf(lock)
  const facts = pair ?? {}
  return (
    <Box data-testid="v3-lock-detail">
      <LockPill lock={lock} pool={pool} pair={facts} />
      {pool && <CountedPool pool={pool} pair={facts} />}
      {lock?.verifiedManually && <Text fontSize="12px" color="textSubtle" mt="6px">{popText(lock)}</Text>}
      {pool && lock && !lock.verifiedManually && (s === 'locked' || s === 'partial' || s === 'unlocked') && (
        <Text fontSize="12px" color="textSubtle" mt="6px" data-testid="v3-lock-line">{lockLine(lock, pool, facts)}</Text>
      )}
      {s === 'unavailable' && (
        <Text fontSize="12px" color="textSubtle" mt="6px">The chain could not be read just now, so no lock is claimed. It is checked again automatically.</Text>
      )}
      {(s === 'unlocked' || s === 'partial') && (
        <Text fontSize="12px" color="textSubtle" mt="6px" data-testid="v3-lock-zero">{ZERO_LINE}</Text>
      )}
      {(s === 'unlocked' || s === 'partial') && <LockLink lock={lock} pool={pool} compact={false} />}
    </Box>
  )
}
